import { EntityId, PayloadAction } from "@reduxjs/toolkit"
import { addSeconds, isBefore } from "date-fns"
import { SagaIterator } from "redux-saga"
import {
  call,
  delay,
  put,
  race,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects"
import { VNZService } from "~/components/cloud-provider/vnz/service/VNZService"
import { VNZAuthTokenEntity } from "~/interfaces/entities/VNZAuthTokenEntity"
import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { PrinticularApi } from "~/services/PrinticularApi"
import { startFetchingVNZCloudImages } from "../process"
import {
  SetIsAuthenticatedPayload,
  VNZAuth,
  VNZ_ACCESS_TOKEN_KEY,
  VNZ_ACCESS_TOKEN_VALIDITY_IN_SECONDS_KEY,
  VNZ_REFRESH_TOKEN_DATE_KEY,
  VNZ_REFRESH_TOKEN_KEY,
} from "../state/VNZAuth"
import {
  LoadThumbnailForImagePayload,
  vnzImages,
  vnzImagesSelectors,
} from "../state/VNZImages"
import { RootState } from "../store"
import { getApi } from "./api"
import { getVnzService } from "./VNZService"

// Cap how many decoded thumbnail blobs are kept alive at once. Cells that scroll
// far out of view get their object URL revoked (freeing memory); the flags are
// reset so the thumbnail re-fetches if the cell is scrolled back into view. This
// keeps blob memory bounded even for libraries of thousands of images.
const THUMBNAIL_CACHE_LIMIT = 800
const loadedThumbnailIds: EntityId[] = []

export function* watchSaveVNZAuthentication() {
  yield takeEvery(
    VNZAuth.actions.setIsAuthenticated.type,
    saveVNZAuthentication
  )
}

function saveVNZAuthentication(
  action: PayloadAction<SetIsAuthenticatedPayload>
) {
  const { payload } = action
  localStorage.setItem(VNZ_ACCESS_TOKEN_KEY, payload.accessToken)
  localStorage.setItem(
    VNZ_ACCESS_TOKEN_VALIDITY_IN_SECONDS_KEY,
    payload.tokenValidityInSeconds.toString()
  )
  localStorage.setItem(VNZ_REFRESH_TOKEN_KEY, payload.refreshToken)
  localStorage.setItem(VNZ_REFRESH_TOKEN_DATE_KEY, payload.lastRefreshDate)
}

export function* watchStartVNZAccessTokenRefresher() {
  yield takeLatest(
    VNZAuth.actions.setIsAuthenticated.type,
    startVNZAccessTokenRefresher
  )
}

const ONE_HOUR_IN_MS = 60 * 60 * 1000
function* startVNZAccessTokenRefresher(): SagaIterator<void> {
  while (true) {
    const { cancelled } = yield race({
      delayed: delay(ONE_HOUR_IN_MS),
      cancelled: take(VNZAuth.actions.logout.type),
    })

    if (cancelled) {
      break
    }

    try {
      yield put(VNZAuth.actions.refreshAuthenticationToken())
    } catch (error) {
      console.error("Error refreshing VNZ access token", error)
      break
    }
  }
}

export function* watchRestoreVNZAuthenticationStateFromLocalStorage() {
  yield takeEvery(
    VNZAuth.actions.restoreAuthenticationStateFromLocalStorage.type,
    restoreVNZAuthenticationStateFromLocalStorage
  )
}

function* restoreVNZAuthenticationStateFromLocalStorage(): SagaIterator<void> {
  const accessToken = localStorage.getItem(VNZ_ACCESS_TOKEN_KEY)
  const tokenValidityInSeconds = localStorage.getItem(
    VNZ_ACCESS_TOKEN_VALIDITY_IN_SECONDS_KEY
  )
  const refreshToken = localStorage.getItem(VNZ_REFRESH_TOKEN_KEY)
  const lastRefreshDate = localStorage.getItem(VNZ_REFRESH_TOKEN_DATE_KEY)

  if (
    accessToken &&
    tokenValidityInSeconds &&
    refreshToken &&
    lastRefreshDate
  ) {
    const tokenExpirationDate = addSeconds(
      new Date(lastRefreshDate),
      parseInt(tokenValidityInSeconds ?? "0")
    )
    const tokenIsValid = isBefore(new Date(), tokenExpirationDate)

    if (tokenIsValid) {
      yield put(
        VNZAuth.actions.setIsAuthenticated({
          accessToken,
          tokenValidityInSeconds: parseInt(tokenValidityInSeconds),
          refreshToken,
          lastRefreshDate,
        })
      )
      // refresh the token and update the auth state if
      // the token is expired but we have a refresh token
      yield put(VNZAuth.actions.refreshAuthenticationToken())
    }
  }
}

export function* watchRefreshVNZAuthenticationToken() {
  yield takeEvery(
    VNZAuth.actions.refreshAuthenticationToken.type,
    refreshVNZAuthenticationToken
  )
}

function* refreshVNZAuthenticationToken(): SagaIterator<void> {
  try {
    const api: PrinticularApi = yield call(getApi)

    const refreshToken = yield select(
      (state: RootState) => state.VNZAuth.refreshToken
    )

    const newTokenResult: VNZAuthTokenEntity = yield retry(
      3, // retry count
      5_000, // delay between retries in ms
      api.refreshVerizonAccessToken,
      refreshToken
    )

    yield put(
      VNZAuth.actions.setIsAuthenticated({
        accessToken: newTokenResult.accessToken,
        tokenValidityInSeconds: newTokenResult.expiresIn,
        refreshToken: newTokenResult.refreshToken,
        lastRefreshDate: new Date().toISOString(),
      })
    )
  } catch (error) {
    console.error("Error refreshing VNZ access token", error)
    yield put(VNZAuth.actions.logout())
  }
}

export function* watchProcessVNZLogout() {
  yield takeEvery(VNZAuth.actions.logout.type, processVNZLogout)
}

function* processVNZLogout(): SagaIterator<void> {
  localStorage.removeItem(VNZ_ACCESS_TOKEN_KEY)
  localStorage.removeItem(VNZ_ACCESS_TOKEN_VALIDITY_IN_SECONDS_KEY)
  localStorage.removeItem(VNZ_REFRESH_TOKEN_KEY)
  localStorage.removeItem(VNZ_REFRESH_TOKEN_DATE_KEY)

  // free all decoded blob object URLs before dropping the entities
  const allImages: VNZImageEntity[] = yield select((state: RootState) =>
    vnzImagesSelectors.selectAll(state)
  )
  allImages.forEach(image => {
    if (image.thumbnailLocalUrl) {
      URL.revokeObjectURL(image.thumbnailLocalUrl)
    }
    if (image.fullImageLocalUrl) {
      URL.revokeObjectURL(image.fullImageLocalUrl)
    }
  })
  loadedThumbnailIds.length = 0

  // remove vnz images
  yield put(vnzImages.actions.removeAll())
}

export function* watchProcessVNZImages() {
  // takeLeading: while one fullview load is running, ignore further triggers.
  // Combined with the guard below this makes the (heavy, ~13MB) fetch idempotent
  // so remounts (e.g. the desktop/mobile modal swap on window resize) can't
  // kick off a duplicate fetch.
  yield takeLeading(startFetchingVNZCloudImages.type, processVNZImages)
}

function* processVNZImages(): SagaIterator<void> {
  try {
    // Skip if a load is already in flight or the images are already loaded.
    const isAlreadyLoading = yield select(
      (state: RootState) => state.VNZImages.isLoading
    )
    const alreadyLoadedCount = yield select((state: RootState) =>
      vnzImagesSelectors.selectTotal(state)
    )
    if (isAlreadyLoading || alreadyLoadedCount > 0) {
      return
    }

    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(vnzImages.actions.setHasError(false))
    yield put(vnzImages.actions.setIsLoading(true))
    const images: VNZImageEntity[] = yield retry(3, 3_000, () =>
      vnzService.fetchAllImages(accessToken)
    )

    // Insert progressively instead of one huge addMany. A single addMany of
    // thousands of entities is one long, blocking task (immer draft work plus a
    // deep auto-freeze of the whole collection), which is why the first render
    // felt far slower than later scroll renders. Paint the first chunk right
    // away, then append the rest a chunk per frame so the main thread stays
    // responsive and the grid appears immediately.
    const IMAGE_INSERT_CHUNK_SIZE = 500
    yield put(
      vnzImages.actions.addMany(images.slice(0, IMAGE_INSERT_CHUNK_SIZE))
    )
    yield put(vnzImages.actions.setIsLoading(false))

    for (
      let i = IMAGE_INSERT_CHUNK_SIZE;
      i < images.length;
      i += IMAGE_INSERT_CHUNK_SIZE
    ) {
      // yield to the browser so it can paint between chunks
      yield delay(250)
      yield put(
        vnzImages.actions.addMany(images.slice(i, i + IMAGE_INSERT_CHUNK_SIZE))
      )
    }
  } catch (error) {
    console.error(error)
    yield put(vnzImages.actions.setHasError(true))
  }
}

export function* watchProcessVNZImageThumbnail() {
  yield takeEvery(
    vnzImages.actions.loadThumbnailForImage.type,
    processVNZImageThumbnail
  )
}

function* processVNZImageThumbnail(
  action: PayloadAction<LoadThumbnailForImagePayload>
): SagaIterator<void> {
  const imageId = action.payload.id
  const image: VNZImageEntity = yield select((state: RootState) =>
    vnzImagesSelectors.selectById(state, imageId)
  )

  try {
    const vnzService: VNZService = yield call(getVnzService)

    const accessToken = yield select(
      (state: RootState) => state.VNZAuth.accessToken
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: { isLoadingThumbnail: true },
      })
    )

    const thumbnailLocalUrl = yield retry(3, 5_000, () =>
      vnzService.fetchThumbnail(accessToken, image)
    )

    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: true,
          thumbnailLocalUrl,
        },
      })
    )

    // LRU eviction of decoded thumbnail blobs to keep memory bounded.
    loadedThumbnailIds.push(image.id)
    if (loadedThumbnailIds.length > THUMBNAIL_CACHE_LIMIT) {
      const evictId = loadedThumbnailIds.shift()
      if (evictId != null && evictId !== image.id) {
        const evicted: VNZImageEntity = yield select((state: RootState) =>
          vnzImagesSelectors.selectById(state, evictId)
        )
        if (evicted?.thumbnailLocalUrl) {
          URL.revokeObjectURL(evicted.thumbnailLocalUrl)
        }
        yield put(
          vnzImages.actions.updateOne({
            id: evictId,
            changes: {
              isLoadingThumbnail: false,
              hasLoadedThumbnail: false,
              hasErrorLoadingThumbnail: false,
              thumbnailLocalUrl: undefined,
            },
          })
        )
      }
    }
  } catch (error) {
    yield put(
      vnzImages.actions.updateOne({
        id: image.id,
        changes: {
          isLoadingThumbnail: false,
          hasLoadedThumbnail: false,
          hasErrorLoadingThumbnail: true,
        },
      })
    )
  }
}
